import { useState, useMemo, useEffect } from 'react';
import { Order } from '@/data/mockData';

interface UseOrdersPaginationReturn {
  currentPage: number;
  totalPages: number;
  currentOrders: Order[];
  startIndex: number;
  endIndex: number;
  setCurrentPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
}

export const useOrdersPagination = (
  sortedOrders: Order[],
  itemsPerPage: number = 10
): UseOrdersPaginationReturn => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when sortedOrders changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortedOrders]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedOrders.length / itemsPerPage);
  }, [sortedOrders.length, itemsPerPage]);

  const startIndex = useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const endIndex = useMemo(() => {
    return startIndex + itemsPerPage;
  }, [startIndex, itemsPerPage]);

  const currentOrders = useMemo(() => {
    return sortedOrders.slice(startIndex, endIndex);
  }, [sortedOrders, startIndex, endIndex]);

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  return {
    currentPage,
    totalPages,
    currentOrders,
    startIndex,
    endIndex,
    setCurrentPage,
    goToNextPage,
    goToPreviousPage,
  };
};

